import { createFileRoute, Link } from "@tanstack/react-router";
import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect, type ReactNode } from "react";

export const Route = createFileRoute("/wrapped/2025")({
  component: Wrapped2025,
  head: () => ({
    meta: [
      { title: "Macrowave by the Numbers: 2025" },
      {
        property: "og:title",
        content: "Macrowave by the Numbers: 2025",
      },
      {
        property: "og:description",
        content: "2,077 broadcasts. 8,612 listeners. 20+ countries. A look at how far we've come.",
      },
      {
        name: "description",
        content: "2,077 broadcasts. 8,612 listeners. 20+ countries. A look at how far we've come.",
      },
    ],
  }),
});

function Wrapped2025() {
  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Header */}
      <header className="fixed left-0 right-0 top-0 z-50 bg-black/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="font-medium text-white hover:text-white/80 transition-colors">
            Macrowave
          </Link>
          <Link to="/" className="text-sm text-white/60 hover:text-white transition-colors">
            ← Back to home
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <Section className="bg-gradient-to-b from-neutral-950 to-neutral-900 pt-20 text-white">
        <div className="flex flex-col items-center justify-center text-center">
          <FadeIn>
            <p className="mb-4 text-sm uppercase tracking-widest text-white/50">
              Macrowave
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1 className="text-[clamp(3rem,12vw,8rem)] font-bold leading-[0.9] tracking-tight">
              2025
            </h1>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-6 text-xl text-white/60">by the numbers</p>
          </FadeIn>
        </div>
      </Section>

      {/* Origin Story */}
      <Section className="bg-white">
        <div className="mx-auto max-w-2xl">
          <FadeIn>
            <p className="text-xl leading-relaxed text-neutral-600">
              It started with a simple message in a group chat:
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <blockquote className="my-12 border-l-4 border-neutral-900 py-4 pl-6 text-2xl font-medium italic text-neutral-900">
              "I want one of you guys to build a p2p radio protocol so I can broadcast whatever I'm listening to."
            </blockquote>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="text-xl leading-relaxed text-neutral-600">
              What happened next surprised us. Lucas and I built something in a weekend.
              Then people started using it. Then more people. Then people we'd never met,
              in countries we'd never been to.
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* Big Numbers */}
      <Section className="bg-neutral-950 text-white">
        <div className="grid gap-12 md:grid-cols-2 md:gap-24">
          <div className="text-center">
            <CountUp value={2077} className="text-[clamp(4rem,15vw,10rem)] font-bold leading-none" />
            <FadeIn delay={0.3}>
              <p className="mt-4 text-xl text-white/60">broadcasts</p>
              <p className="mt-1 text-sm text-white/40">That's 6 broadcasts every single day</p>
            </FadeIn>
          </div>
          <div className="text-center">
            <CountUp value={8612} className="text-[clamp(4rem,15vw,10rem)] font-bold leading-none" />
            <FadeIn delay={0.3}>
              <p className="mt-4 text-xl text-white/60">listeners</p>
              <p className="mt-1 text-sm text-white/40">A small stadium of people</p>
            </FadeIn>
          </div>
        </div>
        <FadeIn delay={0.4}>
          <p className="mx-auto mt-16 max-w-xl text-center text-lg text-white/50">
            Each broadcast a moment where someone thought: I want to share this right now.
          </p>
        </FadeIn>
      </Section>

      {/* Hours */}
      <Section className="bg-gradient-to-b from-neutral-100 to-[#f5f9f5]">
        <div className="text-center">
          <FadeIn>
            <p className="mb-4 text-sm uppercase tracking-widest text-neutral-500">
              Total time shared
            </p>
          </FadeIn>
          <CountUp
            value={2531}
            className="text-[clamp(5rem,20vw,14rem)] font-bold leading-none text-neutral-900"
          />
          <FadeIn delay={0.3}>
            <p className="mt-4 text-2xl text-neutral-600">hours of live connection</p>
          </FadeIn>
          <FadeIn delay={0.35}>
            <p className="mt-2 text-lg text-neutral-400">That's 105 days of non-stop broadcasting</p>
          </FadeIn>
          <FadeIn delay={0.4}>
            <p className="mx-auto mt-8 max-w-lg text-lg text-neutral-500">
              Music sessions at 3am. Creative work in real-time. Friends listening to the same album,
              thousands of miles apart. You can't recreate that with a recording.
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* Countries */}
      <Section className="bg-neutral-950 text-white">
        <div className="text-center">
          <FadeIn>
            <p className="mb-4 text-sm uppercase tracking-widest text-white/50">
              Global reach
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="text-[clamp(4rem,15vw,10rem)] font-bold leading-none">
              20<span className="text-white/40">+</span>
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-4 text-2xl text-white/60">countries</p>
          </FadeIn>
          <FadeIn delay={0.25}>
            <p className="mt-2 text-lg text-white/40">From San Francisco to Jakarta</p>
          </FadeIn>
        </div>

        <div className="mx-auto mt-16 grid max-w-3xl grid-cols-2 gap-6 md:grid-cols-4">
          {[
            { flag: "🇺🇸", name: "USA", count: "4.1K" },
            { flag: "🇮🇩", name: "Indonesia", count: "1.9K" },
            { flag: "🇩🇪", name: "Germany", count: "922" },
            { flag: "🇬🇧", name: "UK", count: "677" },
            { flag: "🇳🇱", name: "Netherlands", count: "388" },
            { flag: "🇨🇳", name: "China", count: "386" },
            { flag: "🇹🇼", name: "Taiwan", count: "361" },
            { flag: "🇨🇦", name: "Canada", count: "308" },
          ].map((country, i) => (
            <FadeIn key={country.name} delay={0.3 + i * 0.05}>
              <div className="text-center">
                <span className="text-4xl">{country.flag}</span>
                <p className="mt-2 font-medium">{country.name}</p>
                <p className="text-sm text-white/50">{country.count}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.7}>
          <p className="mt-12 text-center text-white/40">
            Plus Turkey, Philippines, Spain, Greece, Australia, and more...
          </p>
        </FadeIn>
      </Section>

      {/* Platform */}
      <Section className="bg-gradient-to-b from-[#f0f9f0] to-white">
        <div className="text-center">
          <FadeIn>
            <p className="mb-4 text-sm uppercase tracking-widest text-neutral-500">
              Built for Apple
            </p>
          </FadeIn>
          <CountUp
            value={91}
            suffix="%"
            className="text-[clamp(5rem,20vw,14rem)] font-bold leading-none text-neutral-900"
          />
          <FadeIn delay={0.3}>
            <p className="mt-4 text-2xl text-neutral-600">Apple ecosystem</p>
          </FadeIn>
        </div>

        <div className="mx-auto mt-16 flex max-w-2xl flex-wrap justify-center gap-4">
          {[
            { name: "macOS", value: "49.3%", dark: true },
            { name: "iOS", value: "41.8%", dark: true },
            { name: "Android", value: "5.6%", dark: false },
            { name: "Other", value: "3.3%", dark: false },
          ].map((platform, i) => (
            <FadeIn key={platform.name} delay={0.4 + i * 0.1}>
              <div
                className={`rounded-2xl px-8 py-6 text-center ${
                  platform.dark
                    ? "bg-neutral-900 text-white"
                    : "bg-neutral-100 text-neutral-900"
                }`}
              >
                <p className="text-2xl font-bold">{platform.value}</p>
                <p className={`text-sm ${platform.dark ? "text-white/60" : "text-neutral-500"}`}>
                  {platform.name}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* App Screenshots */}
      <Section className="bg-gradient-to-b from-neutral-950 via-[#0a1a0a] to-neutral-950 text-white">
        <div className="text-center">
          <FadeIn>
            <p className="mb-4 text-sm uppercase tracking-widest text-[#6adf47]/60">
              Broadcasting everywhere
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h2 className="text-4xl font-bold md:text-5xl">One tap to go live</h2>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mx-auto mt-4 max-w-lg text-lg text-white/60">
              Share your screen, share your music. From your Mac or your pocket.
            </p>
          </FadeIn>
        </div>

        <div className="mx-auto mt-16 grid max-w-4xl gap-12 md:grid-cols-2">
          <FadeIn delay={0.3}>
            <div className="text-center">
              <div className="mx-auto max-w-[280px] overflow-hidden rounded-[2.5rem] bg-neutral-900 p-2 shadow-2xl shadow-[#6adf47]/10">
                <img
                  src="/images/iphone-broadcaster.webp"
                  alt="Macrowave iOS App"
                  className="w-full rounded-[2rem]"
                />
              </div>
              <p className="mt-6 text-lg font-medium">iOS App</p>
              <p className="text-sm text-white/50">Broadcast from anywhere</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.4}>
            <div className="text-center">
              <div className="mx-auto max-w-[500px] overflow-hidden rounded-xl bg-neutral-900 p-2 shadow-2xl shadow-[#6adf47]/10">
                <video
                  poster="/images/demo.avif"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full rounded-lg"
                >
                  <source src="/videos/demo.mp4" type="video/mp4" />
                  <img src="/images/demo.webp" alt="Macrowave app demo" />
                </video>
              </div>
              <p className="mt-6 text-lg font-medium">macOS App</p>
              <p className="text-sm text-white/50">Stream your desktop audio</p>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Average Broadcast */}
      <Section className="bg-neutral-100">
        <FadeIn>
          <p className="mb-12 text-center text-sm uppercase tracking-widest text-neutral-500">
            The average broadcast
          </p>
        </FadeIn>

        <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-3">
          <div className="text-center">
            <CountUp value={17} className="text-7xl font-bold text-neutral-900" />
            <FadeIn delay={0.2}>
              <p className="mt-2 text-neutral-600">minutes</p>
              <p className="mt-1 text-sm text-neutral-400">The perfect coffee break</p>
            </FadeIn>
          </div>
          <div className="text-center">
            <CountUp value={5} className="text-7xl font-bold text-neutral-900" />
            <FadeIn delay={0.3}>
              <p className="mt-2 text-neutral-600">listeners</p>
              <p className="mt-1 text-sm text-neutral-400">A room full of friends</p>
            </FadeIn>
          </div>
          <div className="text-center">
            <CountUp value={306} className="text-7xl font-bold text-neutral-900" />
            <FadeIn delay={0.4}>
              <p className="mt-2 text-neutral-600">GB streamed</p>
              <p className="mt-1 text-sm text-neutral-400">That's 75,000 songs</p>
            </FadeIn>
          </div>
        </div>

        <FadeIn delay={0.5}>
          <p className="mx-auto mt-12 max-w-lg text-center text-lg text-neutral-500">
            Not every broadcast needs to be a two-hour production.
            Sometimes seventeen minutes with five friends is exactly what you needed.
          </p>
        </FadeIn>
      </Section>

      {/* Chat Feature */}
      <Section className="bg-gradient-to-b from-neutral-900 to-neutral-950 text-white">
        <div className="mx-auto grid max-w-5xl items-center gap-12 md:grid-cols-2">
          <FadeIn>
            <div className="order-2 md:order-1">
              <p className="mb-4 text-sm uppercase tracking-widest text-[#6adf47]/60">
                Ephemeral by design
              </p>
              <h2 className="text-4xl font-bold md:text-5xl">
                Chat that lives in the moment
              </h2>
              <p className="mt-6 text-lg text-white/60">
                Every broadcast comes with real-time chat. React to the music, share thoughts,
                connect with fellow listeners. When the broadcast ends, the chat disappears.
              </p>
              <p className="mt-4 text-white/40">
                No archives. No receipts. Just the conversation, right now.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="order-1 md:order-2">
              <div className="mx-auto max-w-[400px] overflow-hidden rounded-xl shadow-2xl shadow-[#6adf47]/10 ring-1 ring-white/10">
                <img
                  src="/images/macos-chat.webp"
                  alt="Macrowave chat feature"
                  className="w-full"
                />
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Closing */}
      <Section className="bg-neutral-950 text-white">
        <div className="mx-auto max-w-2xl text-center">
          <FadeIn>
            <h2 className="text-4xl font-bold md:text-5xl">Thank you</h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mt-6 text-xl text-white/60">
              To every broadcaster and listener who made this year what it was.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-4 text-white/40">
              Here's to more shared moments.
            </p>
          </FadeIn>
          <FadeIn delay={0.3}>
            <a
              href="https://apps.apple.com/app/apple-store/id6746954963?pt=123127751&ct=wrapped&mt=8"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-12 inline-block rounded-full bg-[#6adf47] px-8 py-4 text-lg font-medium text-neutral-900 transition-all hover:scale-105 hover:shadow-lg hover:shadow-[#6adf47]/25"
            >
              Start broadcasting
            </a>
          </FadeIn>
        </div>
      </Section>
    </div>
  );
}

// Components

function Section({
  children,
  className = ""
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`flex min-h-screen w-full items-center justify-center px-6 py-24 ${className}`}>
      <div className="w-full max-w-6xl">
        {children}
      </div>
    </section>
  );
}

function FadeIn({
  children,
  delay = 0,
  className = ""
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function CountUp({
  value,
  suffix = "",
  className = ""
}: {
  value: number;
  suffix?: string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    const duration = 2000;
    const steps = 60;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(value * eased));

      if (step >= steps) {
        setDisplay(value);
        clearInterval(timer);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isInView, value]);

  return (
    <motion.span
      ref={ref}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {new Intl.NumberFormat("en-US").format(display)}{suffix}
    </motion.span>
  );
}
